import appConfig from '../../config/appConfig';
import * as errorPageTypes from '../../consts/app/errorPageTypes';
import * as httpErrorsTypes from '../../consts/app/httpErrorsTypes';
import * as gamErrorCodeTypes from '../../consts/gam/gamErrorCodeTypes';
import * as gamMethods from '../../consts/gam/gamMethods';
import {makeSelectGamAuthToken, makeSelectHolderGamAuthToken} from '../../state/selectors/auth';
import {getState} from '../../state/store';
import urlUtils from '../../utils/urlUtils';
import appRouterService from '../route/appRouterService';
import YapClient from '../yap/yapClient';
import yapConnectionService from '../yap/yapConnectionService';
import * as gamGigyaService from './gamGigyaService';

let isGigyaTokenRefreshed = false;

export default class GamClient extends YapClient {
    getHeaders = async (isHolder) => {
        const headers = {
            Accept: 'application/json',
        };

        const state = getState();

        const gamAuthToken = isHolder ? makeSelectHolderGamAuthToken()(state) : makeSelectGamAuthToken()(state);

        if (gamAuthToken) {
            headers.Authorization = 'Bearer ' + gamAuthToken;
        }

        return headers;
    };

    errorCheck = async (
        error,
        methodUrl,
        onSuccess,
        ignoreErrorCodes,
        isNeedRedirectForOtherErrors = true,
        isHolder
    ) => {
        const status = error?.response?.status;
        const errorCode = error?.response?.data?.code;

        switch (status) {
            case httpErrorsTypes.NOT_AUTHORIZED: {
                switch (errorCode) {
                    case gamErrorCodeTypes.GAM_ASSET_NOTAUTHORIZED:
                    case gamErrorCodeTypes.GAM_ACTION_NOTAUTHORIZED:
                        appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
                        return null;
                    case undefined:
                    case null:
                        const isRefreshGigyaToken = methodUrl.includes(gamMethods.REFRESH_TOKEN_PATH);

                        if (isRefreshGigyaToken) {
                            //gigya token already valid but gam token still wrong
                            if (isGigyaTokenRefreshed && this.isTokenRefreshInProgress(isHolder)) {
                                return null;
                            }

                            const isTokenRefreshed = await gamGigyaService.refreshGigyaToken();

                            if (!isTokenRefreshed) {
                                appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
                                return null;
                            }

                            isGigyaTokenRefreshed = true;
                            this.setTokenRefreshInProgress(isHolder, false);
                        } else {
                            isGigyaTokenRefreshed = false;
                        }

                        return this.refreshToken(onSuccess, isHolder);
                    default:
                        this.forwardToGamTryAgainErrorPage(isNeedRedirectForOtherErrors);
                        return null;
                }
            }
            case httpErrorsTypes.CONFLICT:
                await yapConnectionService.forceReconnectToCm(isHolder);
                return;
            case httpErrorsTypes.NOT_FOUND: {
                switch (errorCode) {
                    case gamErrorCodeTypes.GAM_CONSUMER_NOTFOUND:
                    case gamErrorCodeTypes.GAM_DEVICEID_NOTFOUND:
                    case gamErrorCodeTypes.GAM_ASSET_NOTFOUND:
                        appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_SYSTEM_ERROR);
                        break;
                    default:
                        this.forwardToGamTryAgainErrorPage(isNeedRedirectForOtherErrors);
                        break;
                }
                return null;
            }
            default:
                this.forwardToGamTryAgainErrorPage(isNeedRedirectForOtherErrors);
                return null;
        }
    };

    forwardToGamTryAgainErrorPage = (isNeedRedirectForOtherErrors) => {
        if (isNeedRedirectForOtherErrors) {
            appRouterService.forwardToErrorHandlingPage(errorPageTypes.GAM_TEMPORARY_ERROR);
        }
    };

    getRequestParams = async (headers, params, data, isHolder) => {
        const newHeaders = await this.getHeaders(isHolder);

        headers = {...headers, ...newHeaders};
        return {headers, params, data};
    };

    getApiUrl = (methodName) => {
        const gamApiUrl = appConfig.getGamApiUrl();

        return urlUtils.join(gamApiUrl, methodName);
    };

    refreshToken = async (onSuccess, isHolder) => {
        if (this.isTokenRefreshInProgress(isHolder)) return;

        this.setTokenRefreshInProgress(isHolder, true);
        try {
            const isTokenRefreshed = await gamGigyaService.fetchGamToken(isHolder);

            this.setTokenRefreshInProgress(isHolder, false);

            if (isTokenRefreshed) {
                return onSuccess ? onSuccess({refreshedAuthToken: true}) : {data: true};
            }
        } catch (e) {
            this.setTokenRefreshInProgress(isHolder, false);
        }
    };
}
